/** @format */

/* eslint @typescript-eslint/no-base-to-string: "warn" */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import qs from 'qs';
import React, {
    FunctionComponent,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import { normalizeIsoCode } from '@toucan/common/helpers/language';

import { MarketCode, MarketContext } from '@toucan/client/global/market';
import { getParams } from '@toucan/client/global/nextjs';

import {
    ArticleCardData,
    ArticleCollection,
} from '@toucan/client/core/contentful';

import { BasicPageHeader } from '@toucan/client/shared/components/basic-page-header';
import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
} from '@toucan/client/shared/components/constrained-wrapper';
import {
    Pagination,
    PaginationItemProps,
} from '@toucan/client/shared/components/pagination';
import { useMounted } from '@toucan/client/shared/hooks/use-mounted';

import { ArticleResults } from '@toucan/client/features/contentful/components/article-results';

export type ContentfulArticlesResults = {
    results: ArticleCardData[];
    total: number;
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentfulArticlesProps {
    /**
     * The lowercase name for the group of articles
     * @default article
     * (Used for data-testids)
     */
    name?: string;

    /**
     * The heading that gets displayed on the page
     */
    heading?: ReactNode;

    /**
     * Slug used in the url
     */
    slug?: string;

    /**
     * Path to the template used to render items
     */
    templatePath: string;

    /**
     * The initial articles to present
     */
    initialArticles?: ArticleCollection;

    /**
     * Get the data for the articles
     */
    onArticleRequest: (
        numberToFetch: number,
        numberToSkip: number,
        locale: string,
        market: MarketCode,
        tag?: string
    ) => Promise<ContentfulArticlesResults>;

    /**
     * If the parent component is still loading
     */
    isLoading: boolean;

    /**
     * Get the articles based on a category slug
     */
    tag?: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledPaginationWrapper = styled.div`
    text-align: center;
    padding: 2.4rem 0;
`;

const StyledContentfulArticles = styled.div``;

export const ContentfulArticles: FunctionComponent<ContentfulArticlesProps> = ({
    name = 'article',
    heading,
    slug,
    templatePath,
    initialArticles,
    onArticleRequest,
    tag = undefined,
    isLoading,
    'data-testid': dataTestId,
}: ContentfulArticlesProps) => {
    const isMounted = useMounted();
    const { t } = useTranslation();
    const router = useRouter();
    const { activeMarket } = useContext(MarketContext);
    const [pageData, setPageData] = useState<
        ContentfulArticlesResults | undefined
    >(undefined);
    const [isFetchingArticles, setFetchingState] = useState<boolean>(false);

    const { search: queryParts } = getParams(router);
    const { page } = queryParts;

    const contentSlug = t('content', { ns: 'urls' });

    const RESULTS_PER_PAGE = 12;
    const pageNumber = page ? parseInt(page.toString()) : 1;
    const pageIndex = pageNumber - 1;
    const resultTotal = pageData?.total ?? 0;
    const totalPages =
        resultTotal !== -1 ? Math.ceil(resultTotal / RESULTS_PER_PAGE) : -1;

    const paginationItems: PaginationItemProps[] = [];
    for (let i = 0; i < totalPages; i++) {
        paginationItems.push({
            text: `${i + 1}`,
        });
    }

    const articles = pageData?.results ?? [];
    const hasArticles = resultTotal > 0;

    /**
     * Handles the change of page by incrementing the current pageNumber
     * and updating it in the URL along with setting the new results for the page
     */
    async function handlePageChange(pageIndex: number) {
        const newPageNumber = pageIndex === 0 ? 1 : pageIndex + 1;

        setPageData(undefined);

        router.push(
            templatePath,
            `/${activeMarket.slug}/${contentSlug}/${slug}${qs.stringify(
                {
                    page: newPageNumber > 1 ? newPageNumber : undefined,
                },
                { addQueryPrefix: true }
            )}`
        );

        await fetchContentfulArticles(newPageNumber);
    }

    /**
     * Fetch the contentful articles that we want to render on the page
     *
     * @param newPageNumber The page number we want to fetch
     */
    async function fetchContentfulArticles(newPageNumber?: number) {
        if (isLoading) {
            return;
        }

        // If there is no page number then its the initial load
        if (!newPageNumber && initialArticles) {
            setPageData({
                results: initialArticles.items,
                total: initialArticles.total,
            });
            return;
        }

        setPageData(undefined);
        setFetchingState(true);

        const page = newPageNumber ?? pageNumber;

        const { results, total } = await onArticleRequest(
            RESULTS_PER_PAGE,
            (page - 1) * RESULTS_PER_PAGE,
            normalizeIsoCode(activeMarket.iso),
            activeMarket.code,
            tag
        );

        if (isMounted()) {
            setPageData({
                results,
                total,
            });
            setFetchingState(false);
        }
    }

    useEffect(() => {
        fetchContentfulArticles();
    }, [isLoading]);

    return (
        <StyledContentfulArticles data-testid={dataTestId}>
            {heading && (
                <BasicPageHeader data-testid={`${name}__header`}>
                    <Heading size={HeadingSize.H1}>{heading}</Heading>
                </BasicPageHeader>
            )}
            <ConstrainedWrapper gutter={ConstrainedWrapperGutter.LARGE_GUTTER}>
                <ArticleResults
                    loading={isLoading || isFetchingArticles}
                    data-testid={`${name}__collections`}
                    articles={articles}
                />
                {hasArticles && (
                    <StyledPaginationWrapper>
                        <Pagination
                            currentItem={pageIndex}
                            items={paginationItems}
                            itemLimit={5}
                            showFirstLastButtons={true}
                            showPrevNextButtons={true}
                            itemLabel={(item) =>
                                t('Page {{pageNumber}}', {
                                    pageNumber: item + 1,
                                })
                            }
                            onPage={handlePageChange}
                            data-testid={`${name}__pagination`}
                        />
                    </StyledPaginationWrapper>
                )}
            </ConstrainedWrapper>
        </StyledContentfulArticles>
    );
};
